
import { useState } from 'react';

export default function AITutorWidget() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentView, setCurrentView] = useState<'welcome' | 'response'>('welcome');
  const [selectedTopic, setSelectedTopic] = useState<string>('');

  const topics = [
    { id: 'ai', label: 'AI Skills', icon: 'ri-robot-2-line' },
    { id: 'creativity', label: 'Creativity', icon: 'ri-palette-line' },
    { id: 'communication', label: 'Communication', icon: 'ri-chat-3-line' },
    { id: 'marketing', label: 'Marketing', icon: 'ri-megaphone-line' },
    { id: 'leadership', label: 'Leadership', icon: 'ri-team-line' },
    { id: 'unsure', label: 'Not sure yet', icon: 'ri-question-line' },
  ];

  const responses: Record<string, { title: string; message: string }> = {
    ai: {
      title: 'AI Skills',
      message: 'Great choice! Start with AI Productivity Boost to earn your first badge in under 45 minutes.',
    },
    creativity: {
      title: 'Creativity',
      message: 'Perfect! Try Creative Problem Solving to unlock your creative potential and earn your first badge.',
    },
    communication: {
      title: 'Communication',
      message: 'Excellent! Digital Communication Essentials will help you master modern communication skills in just 40 minutes.',
    },
    marketing: {
      title: 'Marketing',
      message: 'Smart choice! Marketing Fundamentals is a quick microcourse that covers the basics and earns you a badge.',
    },
    leadership: {
      title: 'Leadership',
      message: 'Great pick! Leadership Foundations will teach you essential leadership skills in under an hour.',
    },
    unsure: {
      title: 'Not sure yet?',
      message: "No worries! Try Find Your Career Theme. It's a quick microcourse that helps you discover your strengths.",
    },
  };

  const handleTopicClick = (topicId: string) => {
    setSelectedTopic(topicId);
    setCurrentView('response');
  };

  const handleBack = () => {
    setCurrentView('welcome');
    setSelectedTopic('');
  };

  const handleClose = () => {
    setIsOpen(false);
    setTimeout(() => {
      setCurrentView('welcome');
      setSelectedTopic('');
    }, 300);
  };

  return (
    <>
      {/* Floating Button with Subtle Pulsing Glow */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="fixed bottom-8 right-8 w-16 h-16 bg-gradient-to-br from-[#304FFE] to-[#7C4DFF] rounded-full shadow-2xl hover:shadow-[0_20px_40px_rgba(124,77,255,0.4)] hover:scale-110 transition-all duration-300 z-50 flex items-center justify-center group cursor-pointer whitespace-nowrap"
        aria-label="Open AI Tutor"
        style={{
          boxShadow: '0 10px 40px rgba(124, 77, 255, 0.3), 0 0 0 0 rgba(124, 77, 255, 0.4)',
          animation: 'pulse-glow 3s ease-in-out infinite'
        }}
      >
        {isOpen ? (
          <i className="ri-close-line text-3xl text-white"></i>
        ) : (
          <i className="ri-chat-smile-3-line text-3xl text-white group-hover:scale-110 transition-transform duration-300"></i>
        )}
      </button>

      {/* Chat Panel */}
      {isOpen && (
        <>
          {/* Backdrop for mobile */}
          <div
            className="fixed inset-0 bg-black/20 z-40 lg:hidden"
            onClick={handleClose}
          ></div>

          {/* Panel */}
          <div className="fixed bottom-28 right-8 w-[calc(100vw-4rem)] lg:w-[420px] bg-white rounded-3xl shadow-2xl z-50 overflow-hidden animate-slideUp">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#304FFE] to-[#7C4DFF] px-6 py-5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-robot-2-line text-2xl text-white"></i>
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">AI Tutor</h3>
                  <p className="text-white/80 text-xs">Here to help you learn</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors cursor-pointer whitespace-nowrap"
                aria-label="Close"
              >
                <i className="ri-close-line text-xl text-white"></i>
              </button>
            </div>

            {/* Content */}
            <div className="p-6 max-h-[500px] overflow-y-auto">
              {currentView === 'welcome' ? (
                <>
                  {/* Welcome Message */}
                  <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-5 mb-6">
                    <p className="text-gray-800 text-base leading-relaxed">
                      Hi! I'm your AI Tutor. I can help you choose your first microcourse and earn your first badge. What would you like to learn today?
                    </p>
                  </div>

                  {/* Topic Buttons */}
                  <div className="space-y-3">
                    {topics.map((topic) => (
                      <button
                        key={topic.id}
                        onClick={() => handleTopicClick(topic.id)}
                        className="w-full bg-white hover:bg-gradient-to-r hover:from-[#304FFE]/5 hover:to-[#7C4DFF]/5 border-2 border-gray-200 hover:border-[#7C4DFF] rounded-xl px-5 py-4 flex items-center gap-4 transition-all duration-300 group cursor-pointer whitespace-nowrap"
                      >
                        <div className="w-12 h-12 bg-gradient-to-br from-[#304FFE] to-[#7C4DFF] rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                          <i className={`${topic.icon} text-2xl text-white`}></i>
                        </div>
                        <span className="text-gray-800 font-semibold text-base flex-grow text-left">
                          {topic.label}
                        </span>
                        <i className="ri-arrow-right-line text-xl text-gray-400 group-hover:text-[#7C4DFF] group-hover:translate-x-1 transition-all duration-300"></i>
                      </button>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  {/* Response View */}
                  <button
                    onClick={handleBack}
                    className="flex items-center gap-2 text-[#7C4DFF] hover:text-[#304FFE] mb-5 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-arrow-left-line text-lg"></i>
                    <span className="text-sm font-semibold">Back</span>
                  </button>

                  {/* AI Response */}
                  <div className="bg-gradient-to-br from-[#304FFE]/5 to-[#7C4DFF]/5 rounded-2xl p-6 mb-5 border-2 border-[#7C4DFF]/20">
                    <div className="flex items-start gap-3 mb-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-[#304FFE] to-[#7C4DFF] rounded-full flex items-center justify-center flex-shrink-0">
                        <i className="ri-robot-2-line text-xl text-white"></i>
                      </div>
                      <div>
                        <h4 className="text-gray-900 font-bold text-lg mb-1">
                          {responses[selectedTopic]?.title}
                        </h4>
                        <p className="text-gray-700 text-base leading-relaxed">
                          {responses[selectedTopic]?.message}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Action Button */}
                  <button className="w-full bg-gradient-to-r from-[#304FFE] to-[#7C4DFF] text-white font-bold py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap">
                    Start This Microcourse
                  </button>
                </>
              )}
            </div>

            {/* Footer Hint */}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-500 text-center">
                Powered by Praxia AI • Personalized learning for you
              </p>
            </div>
          </div>
        </>
      )}

      {/* Animation Styles */}
      <style>{`
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }
        
        @keyframes pulse-glow {
          0%, 100% {
            box-shadow: 0 10px 40px rgba(124, 77, 255, 0.3), 0 0 0 0 rgba(124, 77, 255, 0.4);
          }
          50% {
            box-shadow: 0 10px 40px rgba(124, 77, 255, 0.4), 0 0 20px 4px rgba(124, 77, 255, 0.15);
          }
        }
      `}</style>
    </>
  );
}
